import { useQuery } from '@tanstack/react-query';
import { useAuth } from '../contexts/AuthContext';
import { 
  Activity, 
  HardDrive, 
  Clock, 
  Zap,
  RefreshCw,
  Server,
  Database,
  TrendingUp,
  AlertTriangle
} from 'lucide-react';

interface SystemMetrics {
  kernel_version: string;
  php_version: string;
  memory_usage: number;
  memory_peak: number;
  memory_limit: string;
  uptime: number;
}

interface Metric {
  name: string;
  value: number;
  labels: Record<string, string>;
  timestamp: number;
}

interface MetricsResponse {
  system: SystemMetrics;
  metrics: Metric[];
  collected_at: string;
}

interface HealthResponse {
  status: string;
  checks: Record<string, any>;
  uptime: number;
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 B';
  const k = 1024;
  const sizes = ['B', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
}

function formatUptime(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ${Math.floor(seconds % 60)}s`;
  const hours = Math.floor(seconds / 3600);
  const mins = Math.floor((seconds % 3600) / 60);
  return `${hours}h ${mins}m`;
}

export default function Metrics() {
  const { token } = useAuth();

  const { data: metrics, isLoading: metricsLoading, refetch: refetchMetrics } = useQuery<MetricsResponse>({
    queryKey: ['kernel-metrics'],
    queryFn: async () => {
      const response = await fetch('/api/v1/kernel/metrics', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch metrics');
      return response.json();
    },
    refetchInterval: 5000
  });

  const { data: health, isLoading: healthLoading } = useQuery<HealthResponse>({
    queryKey: ['kernel-health'],
    queryFn: async () => {
      const response = await fetch('/api/v1/kernel/health', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch health');
      return response.json();
    },
    refetchInterval: 10000
  });

  const { data: status } = useQuery({
    queryKey: ['kernel-status'],
    queryFn: async () => {
      const response = await fetch('/api/v1/kernel/status', {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (!response.ok) throw new Error('Failed to fetch status');
      return response.json();
    },
    refetchInterval: 5000
  });

  if (metricsLoading || healthLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  const memoryPercent = metrics?.system ? 
    (metrics.system.memory_usage / parseMemoryLimit(metrics.system.memory_limit)) * 100 : 0;

  function parseMemoryLimit(limit: string): number {
    const match = limit.match(/^(\d+)([KMG]?)$/i);
    if (!match) return 256 * 1024 * 1024;
    const num = parseInt(match[1]);
    const unit = match[2].toUpperCase();
    switch (unit) {
      case 'G': return num * 1024 * 1024 * 1024;
      case 'M': return num * 1024 * 1024;
      case 'K': return num * 1024;
      default: return num;
    }
  }

  // Group metrics by category
  const groupedMetrics: Record<string, Metric[]> = {};
  metrics?.metrics?.forEach(m => {
    const category = m.name.split('_')[0] || 'other';
    if (!groupedMetrics[category]) groupedMetrics[category] = [];
    groupedMetrics[category].push(m);
  });

  return (
    <div className="px-4 sm:px-6 lg:px-8">
      {/* Header */}
      <div className="sm:flex sm:items-center sm:justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Kernel Metrics</h1>
          <p className="mt-1 text-sm text-gray-500">
            Real-time performance monitoring and system health
          </p>
        </div>
        <button
          onClick={() => refetchMetrics()}
          className="mt-4 sm:mt-0 inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4 mr-2" />
          Refresh
        </button>
      </div>

      {/* Health Status Banner */}
      <div className={`mb-6 p-4 rounded-lg ${
        health?.status === 'healthy' ? 'bg-green-50 border border-green-200' :
        health?.status === 'warning' ? 'bg-yellow-50 border border-yellow-200' :
        'bg-red-50 border border-red-200'
      }`}>
        <div className="flex items-center">
          <div className={`flex-shrink-0 ${
            health?.status === 'healthy' ? 'text-green-400' :
            health?.status === 'warning' ? 'text-yellow-400' :
            'text-red-400'
          }`}>
            {health?.status === 'healthy' ? (
              <Activity className="h-6 w-6" />
            ) : (
              <AlertTriangle className="h-6 w-6" />
            )}
          </div>
          <div className="ml-3">
            <h3 className={`text-sm font-medium ${
              health?.status === 'healthy' ? 'text-green-800' :
              health?.status === 'warning' ? 'text-yellow-800' :
              'text-red-800'
            }`}>
              System Status: {health?.status?.toUpperCase() || 'UNKNOWN'}
            </h3>
            <p className="text-sm text-gray-600 mt-1">
              Kernel v{metrics?.system?.kernel_version} • PHP {metrics?.system?.php_version}
            </p>
          </div>
        </div>
      </div>

      {/* System Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        {/* Memory Usage */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <HardDrive className="h-6 w-6 text-blue-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Memory Usage</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatBytes(metrics?.system?.memory_usage || 0)}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4">
              <div className="flex justify-between text-xs text-gray-500 mb-1">
                <span>Used</span>
                <span>{memoryPercent.toFixed(1)}%</span>
              </div>
              <div className="w-full bg-gray-200 rounded-full h-2">
                <div 
                  className={`h-2 rounded-full ${memoryPercent > 80 ? 'bg-red-500' : memoryPercent > 60 ? 'bg-yellow-500' : 'bg-blue-500'}`}
                  style={{ width: `${Math.min(memoryPercent, 100)}%` }}
                />
              </div>
              <div className="text-xs text-gray-400 mt-1">
                Peak: {formatBytes(metrics?.system?.memory_peak || 0)} / Limit: {metrics?.system?.memory_limit}
              </div>
            </div>
          </div>
        </div>

        {/* Uptime */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Clock className="h-6 w-6 text-green-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Request Time</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {formatUptime(metrics?.system?.uptime || 0)}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              <div>Boot ID: {status?.boot_id?.slice(0, 20) || 'N/A'}...</div>
              <div>Booted: {status?.booted ? 'Yes' : 'No'}</div>
            </div>
          </div>
        </div>

        {/* Syscalls */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Zap className="h-6 w-6 text-yellow-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">Syscalls Registered</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {status?.syscalls_registered || 0}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              <div>Processes: {status?.processes_running || 0}</div>
            </div>
          </div>
        </div>

        {/* Connections */}
        <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Database className="h-6 w-6 text-purple-500" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">DB Connections</dt>
                  <dd className="text-lg font-semibold text-gray-900">
                    {health?.checks?.database?.connections || 1}
                  </dd>
                </dl>
              </div>
            </div>
            <div className="mt-4 text-xs text-gray-500">
              <div>Status: {health?.checks?.database?.status || 'unknown'}</div>
            </div>
          </div>
        </div>
      </div>

      {/* Metrics Table */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900 flex items-center">
            <TrendingUp className="w-5 h-5 mr-2 text-gray-400" />
            Collected Metrics
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            Last updated: {metrics?.collected_at ? new Date(metrics.collected_at).toLocaleString() : 'N/A'}
          </p>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Metric</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Value</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Labels</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {metrics?.metrics?.length ? metrics.metrics.map((metric, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                    {metric.name}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {typeof metric.value === 'number' ? 
                      (metric.name.includes('bytes') ? formatBytes(metric.value) : 
                       metric.name.includes('seconds') ? formatUptime(metric.value) :
                       metric.value.toLocaleString()) 
                      : metric.value}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                    {Object.keys(metric.labels || {}).length > 0 ? (
                      <span className="inline-flex flex-wrap gap-1">
                        {Object.entries(metric.labels).map(([k, v]) => (
                          <span key={k} className="px-2 py-0.5 bg-gray-100 rounded text-xs">
                            {k}={v}
                          </span>
                        ))}
                      </span>
                    ) : '-'}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={3} className="px-6 py-4 text-center text-sm text-gray-500">
                    No metrics collected yet
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Health Checks */}
      {health?.checks && (
        <div className="mt-8 bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
            <h3 className="text-lg font-medium text-gray-900 flex items-center">
              <Server className="w-5 h-5 mr-2 text-gray-400" />
              Health Checks
            </h3>
          </div>
          <div className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {Object.entries(health.checks).map(([name, check]: [string, any]) => (
                <div key={name} className={`p-4 rounded-lg border ${
                  check.status === 'healthy' ? 'border-green-200 bg-green-50' :
                  check.status === 'warning' ? 'border-yellow-200 bg-yellow-50' :
                  'border-red-200 bg-red-50'
                }`}>
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-900 capitalize">{name}</span>
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      check.status === 'healthy' ? 'bg-green-100 text-green-800' :
                      check.status === 'warning' ? 'bg-yellow-100 text-yellow-800' :
                      'bg-red-100 text-red-800'
                    }`}>
                      {check.status}
                    </span>
                  </div>
                  {check.message && (
                    <p className="mt-2 text-sm text-gray-600">{check.message}</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
